import React, { useState, useEffect } from "react";
import { Lock, Check, X } from "lucide-react";
import { useSendEmail } from "../hooks/useSendEmail";

interface NoAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName?: string | undefined;
  ownerEmail?: string | undefined;
  ownerName?: string | undefined;
  requesterName?: string | undefined;
  requesterEmail?: string | undefined;
  autoSendRequest?: boolean;
}

const NoAccessModal: React.FC<NoAccessModalProps> = ({
  isOpen,
  onClose,
  documentName,
  ownerEmail,
  ownerName,
  requesterName,
  requesterEmail,
  autoSendRequest = false,
}) => {
  const { sendEmail } = useSendEmail();
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendAccessRequest = async () => {
    if (!ownerEmail || !requesterEmail) return;

    setIsRequesting(true);
    setError(null);

    try {
      await sendEmail({
        to: ownerEmail,
        template: {
          subject: `Access Request: ${documentName || "Document"}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #1d365d;">Document Access Request</h2>
              <p><strong>${requesterName || requesterEmail}</strong> has requested access to the document <strong>"${documentName || "Untitled Document"}"</strong>.</p>
              <p>You can grant access by opening the document in Data Revival and sharing it with them.</p>
              <p style="color: #666; font-size: 14px; margin-top: 20px;">Requester email: ${requesterEmail}</p>
            </div>
          `,
          text: `${requesterName || requesterEmail} has requested access to the document "${documentName || "Untitled Document"}". You can grant access by opening the document in Data Revival and sharing it with them. Requester email: ${requesterEmail}`,
        },
      });
      setRequestSent(true);
    } catch (err) {
      setError("Failed to send request. Please try again.");
    } finally {
      setIsRequesting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const sendAccessRequest = async () => {
      if (isRequesting || !ownerEmail) return;
      setIsRequesting(true);
      setError(null);

      try {
        await sendEmail({
          to: ownerEmail,
          template: {
            subject: `Access Request for Document: ${documentName || "Untitled Document"}`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #1d365d;">Document Access Request</h2>
                <p><strong>${requesterName || requesterEmail}</strong> has requested access to the document <strong>"${documentName || "Untitled Document"}"</strong>.</p>
                <p>You can grant access by opening the document in Data Revival and sharing it with them.</p>
                <p style="color: #666; font-size: 14px; margin-top: 20px;">Requester email: ${requesterEmail}</p>
              </div>
            `,
            text: `${requesterName || requesterEmail} has requested access to the document "${documentName || "Untitled Document"}". You can grant access by opening the document in Data Revival and sharing it with them. Requester email: ${requesterEmail}`,
          },
        });
        if (!cancelled) {
          setRequestSent(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to send request. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setIsRequesting(false);
        }
      }
    };

    if (isOpen && autoSendRequest && !requestSent && !isRequesting && ownerEmail) {
      sendAccessRequest();
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, autoSendRequest]);

  useEffect(() => {
    if (!isOpen) {
      setRequestSent(false);
      setError(null);
    }
  }, [isOpen]);

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <div
      id="noaccessmodal"
      tabIndex={-1}
      className={`fixed inset-0 z-50 flex justify-center items-start overflow-y-auto transition-opacity duration-200 ${
        visible ? "bg-black/50 opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      <div className="relative p-4 w-full max-w-md mt-20">
        <div className={`relative bg-white rounded-lg border border-gray-200 p-5 transition-all duration-200 ${
          visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}>
          <button
            type="button"
            className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            onClick={onClose}
          >
            <X className="w-4 h-4" aria-hidden="true" />
            <span className="sr-only">Close modal</span>
          </button>

          <div className="text-center pt-2">
            {requestSent ? (
              <>
                <Check className="mx-auto mb-4 text-green-500 w-10 h-10" />
                <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-1">
                  Request sent
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Your access request has been sent to{" "}
                  {ownerName || "the document owner"}. They will be notified via email.
                </p>
                <button
                  type="button"
                  className="h-9 px-5 text-sm font-medium text-white bg-[#1d365d] hover:bg-[#2d4a75] rounded-lg transition-colors"
                  onClick={onClose}
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <Lock className="mx-auto mb-4 text-gray-300 w-10 h-10" />
                <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-1">
                  Access restricted
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  You don't have permission to access
                  {documentName ? (
                    <> <strong className="text-gray-700">"{documentName}"</strong></>
                  ) : (
                    " this document"
                  )}.
                </p>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
                    {error}
                  </p>
                )}

                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    className="h-9 px-4 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  {ownerEmail && (
                    <button
                      type="button"
                      className="h-9 px-4 text-sm font-medium text-white bg-[#1d365d] hover:bg-[#2d4a75] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={sendAccessRequest}
                      disabled={isRequesting}
                    >
                      {isRequesting ? "Sending..." : "Request access"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoAccessModal;
